import React, { useMemo, useState } from 'react';
import { Box, Chip, Divider, Tooltip, Typography } from '@mui/material';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import * as geojson from 'geojson';
import { useUrbanAnimation } from '../contexts/UrbanAnimationContext';
import { UrbanAssetRadiantExposure } from '../types/FireSimulation';

/**
 * "Radiant heat" section of the asset popover: what the selected simulation's
 * fire did to this building. Reads the solver's per-building exposure record
 * (`urban_asset_radiant_exposure`) plus ignition time/mode off the urban
 * animation context, so it only renders while a simulation with the urban
 * model is selected on the map.
 */

const ACCENT = '#00d4ad';
const MUTED = 'rgba(255,255,255,0.55)';
const FAINT = 'rgba(255,255,255,0.35)';

const MODE_LABELS: Record<string, string> = {
  conv: 'flame contact from a neighbouring structure',
  rad: 'radiant heat from a neighbouring structure',
  ember: 'embers from a neighbouring structure',
  cond: 'fire spreading within the structure',
  'wildland-rad': 'radiant heat from the bushfire',
  'wildland-ember': 'embers from the bushfire',
};

const fmtFlux = (kw: number): string =>
  kw >= 100 ? kw.toFixed(0) : kw >= 10 ? kw.toFixed(1) : kw.toFixed(kw >= 1 ? 1 : 2);

/** "+1 h 20 min" style elapsed time. */
const fmtElapsed = (s: number): string => {
  if (!Number.isFinite(s) || s < 0) return '—';
  const m = Math.round(s / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `+${mm} min`;
  return mm === 0 ? `+${h} h` : `+${h} h ${mm} min`;
};

/** Duration for the band table: "0", "4 min", "1 h 05 min". */
const fmtDuration = (s: number): string => {
  if (!s || s <= 0) return '0';
  if (s < 60) return '< 1 min';
  const m = Math.round(s / 60);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm} min`;
  return `${h} h ${String(mm).padStart(2, '0')} min`;
};

interface SparklineProps {
  series: number[];
  frameTimes: number[] | null;
  critical: number;
  currentFrame: number | null;
  ignitionS: number | null;
  wallClock: (s: number) => string | null;
}

/**
 * Single-series trace of peak flux per output frame. One hue (the popover
 * accent), a dotted line at the ignition threshold, the current animation
 * frame as a vertical cursor, and the ignition moment as an orange tick.
 * Hovering reads out the frame under the pointer.
 */
const Sparkline: React.FC<SparklineProps> = ({
  series, frameTimes, critical, currentFrame, ignitionS, wallClock,
}) => {
  const [hover, setHover] = useState<number | null>(null);
  const W = 284;
  const H = 64;
  const PAD_L = 4;
  const PAD_R = 4;
  const PAD_T = 10;
  const PAD_B = 14;
  const n = series.length;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;
  const yMax = Math.max(Math.max(...series), critical, 1) * 1.1;
  const xAt = (i: number) => PAD_L + (n > 1 ? (i / (n - 1)) * innerW : innerW / 2);
  const yAt = (v: number) => PAD_T + innerH - (Math.max(v, 0) / yMax) * innerH;
  const timeAt = (i: number): number | null =>
    frameTimes && frameTimes.length === n ? frameTimes[i] : null;

  const linePath = series.map((v, i) => `${i === 0 ? 'M' : 'L'}${xAt(i).toFixed(1)},${yAt(v).toFixed(1)}`).join(' ');
  const areaPath = `${linePath} L${xAt(n - 1).toFixed(1)},${yAt(0).toFixed(1)} L${xAt(0).toFixed(1)},${yAt(0).toFixed(1)} Z`;

  let peakIdx = 0;
  series.forEach((v, i) => { if (v > series[peakIdx]) peakIdx = i; });

  // Ignition tick: position by time when frame times are known.
  let ignX: number | null = null;
  if (ignitionS != null && frameTimes && frameTimes.length === n && n > 1) {
    const t0 = frameTimes[0];
    const t1 = frameTimes[n - 1];
    if (t1 > t0) ignX = PAD_L + ((ignitionS - t0) / (t1 - t0)) * innerW;
  }

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((px - PAD_L) / innerW) * (n - 1));
    setHover(Math.max(0, Math.min(n - 1, i)));
  };

  const readIdx = hover ?? (currentFrame != null && currentFrame < n ? currentFrame : null);
  const readT = readIdx != null ? timeAt(readIdx) : null;

  return (
    <Box sx={{ mt: 1 }}>
      <svg
        width="100%"
        viewBox={`0 0 ${W} ${H}`}
        style={{ display: 'block', cursor: 'crosshair' }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label="Radiant heat flux over the simulation"
      >
        <path d={areaPath} fill={ACCENT} opacity={0.14} />
        <path d={linePath} fill="none" stroke={ACCENT} strokeWidth={2} strokeLinejoin="round" />
        {critical > 0 && critical < yMax && (
          <>
            <line x1={PAD_L} x2={W - PAD_R} y1={yAt(critical)} y2={yAt(critical)} stroke={FAINT} strokeWidth={1} strokeDasharray="2 3" />
            <text x={W - PAD_R} y={yAt(critical) - 2} textAnchor="end" fontSize={8} fill={MUTED}>
              ignites {fmtFlux(critical)}
            </text>
          </>
        )}
        {ignX != null && ignX >= PAD_L && ignX <= W - PAD_R && (
          <line x1={ignX} x2={ignX} y1={PAD_T} y2={PAD_T + innerH} stroke="#ff6b00" strokeWidth={1.5} />
        )}
        {currentFrame != null && currentFrame < n && hover == null && (
          <line x1={xAt(currentFrame)} x2={xAt(currentFrame)} y1={PAD_T} y2={PAD_T + innerH} stroke="rgba(255,255,255,0.7)" strokeWidth={1} />
        )}
        {hover != null && (
          <line x1={xAt(hover)} x2={xAt(hover)} y1={PAD_T} y2={PAD_T + innerH} stroke="rgba(255,255,255,0.9)" strokeWidth={1} />
        )}
        {series[peakIdx] > 0 && (
          <>
            <circle cx={xAt(peakIdx)} cy={yAt(series[peakIdx])} r={3} fill={ACCENT} stroke="#212b37" strokeWidth={2} />
            <text
              x={Math.min(Math.max(xAt(peakIdx), 16), W - 16)}
              y={Math.max(yAt(series[peakIdx]) - 5, 8)}
              textAnchor="middle" fontSize={8} fill="rgba(255,255,255,0.85)"
            >
              {fmtFlux(series[peakIdx])}
            </text>
          </>
        )}
        {readIdx != null && (
          <circle cx={xAt(readIdx)} cy={yAt(series[readIdx])} r={3} fill="#fff" stroke="#212b37" strokeWidth={1.5} />
        )}
        <text x={PAD_L} y={H - 3} fontSize={8} fill={MUTED}>
          {frameTimes && frameTimes.length ? fmtElapsed(frameTimes[0]).replace('+', 'start ') : 'start'}
        </text>
        <text x={W - PAD_R} y={H - 3} textAnchor="end" fontSize={8} fill={MUTED}>
          {frameTimes && frameTimes.length === n ? fmtElapsed(frameTimes[n - 1]) : 'end'}
        </text>
      </svg>
      <Typography sx={{ fontSize: '0.68rem', color: MUTED, minHeight: 16 }}>
        {readIdx != null
          ? `${readT != null ? fmtElapsed(readT) : `frame ${readIdx + 1}`}${readT != null && wallClock(readT) ? ` (${wallClock(readT)})` : ''}: ${fmtFlux(series[readIdx])} kW/m²`
          : 'Hover the trace to read a value'}
      </Typography>
    </Box>
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; hint?: string }> = ({ label, value, hint }) => (
  <Box>
    <Typography sx={{ fontSize: '0.62rem', color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>
      {label}
    </Typography>
    <Tooltip title={hint ?? ''} disableHoverListener={!hint} placement="top">
      <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, lineHeight: 1.3 }}>{value}</Typography>
    </Tooltip>
  </Box>
);

const AssetRadiantExposure: React.FC<{ asset: geojson.Feature }> = ({ asset }) => {
  const { selectedIgnitionInfo, timestamps, currentFrame } = useUrbanAnimation();

  const idKey = String(asset.id ?? asset.properties?.id ?? asset.properties?.__id ?? '');
  const exposure: UrbanAssetRadiantExposure | undefined =
    selectedIgnitionInfo?.urban_asset_radiant_exposure?.[idKey];
  const ignitionS: number | null = selectedIgnitionInfo?.urban_asset_ignition_s?.[idKey] ?? null;
  const ignitionMode: string | null = selectedIgnitionInfo?.urban_asset_ignition_mode?.[idKey] ?? null;
  const modelledIds: number[] | undefined = selectedIgnitionInfo?.urban_asset_ids;

  // Frame times for the trace: the context carries wall-clock timestamps per
  // frame, which also give the "(14:35)" readouts.
  const frameTimes = useMemo<number[] | null>(() => {
    if (!timestamps.length) return null;
    return timestamps.map((t) => t.diff(timestamps[0], 'second'));
  }, [timestamps]);
  const wallClock = (s: number): string | null =>
    timestamps.length ? timestamps[0].add(s, 'second').format('HH:mm') : null;

  if (!selectedIgnitionInfo) return null;
  // Only buildings the urban model governs have an exposure record to show.
  const isModelled =
    !modelledIds || modelledIds.length === 0 || modelledIds.some((m) => String(m) === idKey);
  if (!isModelled) return null;
  const hasAnything = exposure || ignitionS != null;
  if (!hasAnything && !selectedIgnitionInfo.urban_asset_ignition_s) return null;

  const when = (s: number) => {
    const wc = wallClock(s);
    return wc ? `${fmtElapsed(s)} (${wc})` : fmtElapsed(s);
  };

  const outcome = ignitionS != null
    ? `Ignited ${when(ignitionS)}${ignitionMode && MODE_LABELS[ignitionMode] ? ` by ${MODE_LABELS[ignitionMode]}` : ''}.`
    : 'Did not ignite in this simulation.';

  const overThreshold = exposure ? exposure.peak_kw_m2 >= exposure.critical_kw_m2 : false;
  const pctOfExposed = (s: number) =>
    exposure && exposure.exposed_s > 0 ? Math.round((100 * s) / exposure.exposed_s) : 0;

  return (
    <Box sx={{ mt: 1.5 }}>
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.12)', mb: 1.25 }} />
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <WhatshotIcon sx={{ mr: 1, fontSize: 18, color: ACCENT }} />
        <Typography variant="body2" sx={{ fontWeight: 600 }}>Radiant heat</Typography>
        <Typography variant="caption" sx={{ ml: 0.75, color: MUTED }}>selected simulation</Typography>
      </Box>

      {!exposure ? (
        <Typography variant="body2" sx={{ color: MUTED, fontSize: '0.8rem' }}>
          {ignitionS != null
            ? `No measurable radiant heat reached this building before it ignited. ${outcome}`
            : 'No radiant heat reached this building in the selected simulation.'}
        </Typography>
      ) : (
        <>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, lineHeight: 1 }}>
              {fmtFlux(exposure.peak_kw_m2)}
              <Typography component="span" sx={{ fontSize: '0.75rem', color: MUTED, ml: 0.5 }}>kW/m² peak</Typography>
            </Typography>
            <Chip
              size="small"
              label={overThreshold ? 'above ignition threshold' : 'below ignition threshold'}
              sx={{
                height: 20, fontSize: '0.68rem', fontWeight: 600,
                color: overThreshold ? '#111' : 'rgba(255,255,255,0.85)',
                bgcolor: overThreshold ? '#ff6b00' : 'rgba(255,255,255,0.1)',
                '& .MuiChip-label': { px: 0.9 },
              }}
            />
          </Box>
          <Typography sx={{ fontSize: '0.72rem', color: MUTED, mt: 0.25 }}>
            at {when(exposure.peak_s)} · this building ignites at {fmtFlux(exposure.critical_kw_m2)} kW/m²
          </Typography>

          {/* Flux distribution over the exposed time (time-weighted). */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, mt: 1.25 }}>
            <Stat label="Min" value={fmtFlux(exposure.min_kw_m2)} hint="Lowest flux while exposed, kW/m²" />
            <Stat label="Median" value={fmtFlux(exposure.median_kw_m2)} hint="Time-weighted median flux while exposed, kW/m²" />
            <Stat label="Mean" value={fmtFlux(exposure.mean_kw_m2)} hint="Time-weighted mean flux while exposed, kW/m²" />
            <Stat
              label="10th–90th"
              value={`${fmtFlux(exposure.p10_kw_m2)}–${fmtFlux(exposure.p90_kw_m2)}`}
              hint="Time-weighted 10th to 90th percentile flux while exposed, kW/m²"
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1, mt: 1.25 }}>
            <Stat
              label="Exposed for"
              value={fmtDuration(exposure.exposed_s)}
              hint={`Time receiving any radiant heat, from ${fmtElapsed(exposure.first_exposed_s)}`}
            />
            <Stat
              label="Above threshold"
              value={
                exposure.above_critical_s > 0
                  ? `${fmtDuration(exposure.above_critical_s)} (${pctOfExposed(exposure.above_critical_s)}%)`
                  : 'never'
              }
              hint={`Time at or above this building's ignition threshold of ${fmtFlux(exposure.critical_kw_m2)} kW/m², as a share of the exposed time`}
            />
            <Stat
              label="Radiant dose"
              value={`${exposure.dose_mj_m2 < 10 ? exposure.dose_mj_m2.toFixed(2) : exposure.dose_mj_m2.toFixed(1)} MJ/m²`}
              hint="Radiant heat integrated over the exposed time"
            />
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 1.25 }}>
            <Stat
              label="Peak from bushfire"
              value={`${fmtFlux(exposure.peak_wildland_kw_m2)} kW/m²`}
              hint="Highest flux from the bushfire front alone"
            />
            <Stat
              label="Peak from neighbours"
              value={`${fmtFlux(exposure.peak_neighbour_kw_m2)} kW/m²`}
              hint="Highest flux from burning neighbouring buildings and trees"
            />
          </Box>

          {exposure.series_kw_m2?.length > 1 && (
            <Sparkline
              series={exposure.series_kw_m2}
              frameTimes={frameTimes && frameTimes.length === exposure.series_kw_m2.length ? frameTimes : null}
              critical={exposure.critical_kw_m2}
              currentFrame={currentFrame}
              ignitionS={ignitionS}
              wallClock={wallClock}
            />
          )}

          <Typography sx={{ fontSize: '0.78rem', mt: 1, color: ignitionS != null ? '#ffb380' : 'rgba(255,255,255,0.85)' }}>
            {outcome}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default AssetRadiantExposure;
